import React, { useEffect, useMemo } from "react";
import useTranslator from "../../../hooks/useTranslator";
import { Button } from "../../../components/design";
import { useImageQualityCheck } from "../../../Utils/imageQuality/useImageQualityCheck";
import { qualityText } from "../../../Utils/imageQuality";

interface ReviewScreenProps {
  image: File | Blob;
  side: "front" | "back";
  error: string | null;
  onConfirm: () => void;
  onRetake: () => void;
}

const getTranslatedText = (
  _t: (key: string, context?: string) => string,
  key: string,
  fallback: string
) => {
  return _t(key);
};

const ReviewScreen: React.FC<ReviewScreenProps> = ({
  image,
  side,
  error,
  onConfirm,
  onRetake,
}) => {
  const { _t } = useTranslator();

  const quality = useImageQualityCheck(image, { mode: "camera", side, _t });
  const blockedByQuality =
    quality.status === "checking" ||
    (quality.status === "fail" && !quality.canOverride);

  const imageUrl = useMemo(() => URL.createObjectURL(image), [image]);

  useEffect(() => () => URL.revokeObjectURL(imageUrl), [imageUrl]);

  const title =
    side === "front"
      ? getTranslatedText(_t, "doesFrontLookGood", "Does the front look good?")
      : getTranslatedText(_t, "doesBackLookGood", "Does the back look good?");

  return (
    <div className="mobile-capture-review-screen">
      <div className="mobile-capture-review-header">
        <span>{getTranslatedText(_t, "photoReady", "Photo ready")}</span>
        <h3>{title}</h3>
      </div>

      <div className="mobile-capture-review-image">
        <img src={imageUrl} alt={_t("mobileCaptureCapturedImage")} />
      </div>

      <p className="mobile-capture-review-hint">
        {getTranslatedText(
          _t,
          "checkReadableNoReflections",
          "Check that everything is readable and there are no reflections"
        )}
      </p>

      {quality.status === "checking" && (
        <p className="mobile-capture-review-hint">
          {qualityText(_t, "imageQualityChecking")}
        </p>
      )}

      {quality.status === "fail" && (
        <div className="mobile-capture-review-error">
          {quality.messages.map((message) => (
            <p key={message}>{message}</p>
          ))}
        </div>
      )}

      {error && (
        <div className="mobile-capture-review-error">
          <p>{error}</p>
        </div>
      )}

      <div className="mobile-capture-review-actions">
        <Button size="lg" block variant="secondary" onClick={onRetake}>
          {getTranslatedText(_t, "retake", "Retake")}
        </Button>
        <Button size="lg" block onClick={onConfirm} disabled={blockedByQuality}>
          {quality.status === "fail"
            ? qualityText(_t, "imageQualityUseAnyway")
            : getTranslatedText(_t, "usePhoto", "Use photo")}
        </Button>
      </div>
    </div>
  );
};

export default ReviewScreen;
